import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { format } from 'date-fns';
import RecommendedTopicCard from '../../components/education/RecommendedTopicCard';
import RecommendationEmptyState from '../../components/education/RecommendationEmptyState';
import AssignedLearningPanel from '../../components/education/AssignedLearningPanel';
import { buildLearningTopicPath } from '../../lib/educationContent';
import { buildTopicRecommendations } from '../../lib/learningRecommendations';

const ClientDashboard = () => {
  const { profile } = useAuth();
  const navigate = useNavigate();

  const [client, setClient] = useState(null);
  const [matters, setMatters] = useState([]);
  const [progressByMatter, setProgressByMatter] = useState({});
  const [recommendations, setRecommendations] = useState([]);
  const [topicRecommendations, setTopicRecommendations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchClientDashboard = async () => {
      if (!profile?.id || !profile?.advisor_firm_id) return;

      try {
        setLoading(true);
        setError(null);

        const { data: clientData, error: clientErr } = await supabase
          .from('clients')
          .select('*')
          .eq('user_id', profile.id)
          .eq('advisor_firm_id', profile.advisor_firm_id)
          .single();

        if (clientErr || !clientData) {
          throw new Error('Your client profile is not linked yet. Please contact your advisor.');
        }

        setClient(clientData);

        const { data: matterData, error: matterErr } = await supabase
          .from('matters')
          .select('*')
          .eq('client_id', clientData.id)
          .eq('advisor_firm_id', profile.advisor_firm_id)
          .order('created_at', { ascending: false });

        if (matterErr) throw matterErr;

        const matterRows = matterData || [];
        setMatters(matterRows);

        if (matterRows.length > 0) {
          const { data: progressData, error: progressErr } = await supabase
            .from('matter_intake_progress')
            .select('*')
            .in('matter_id', matterRows.map(m => m.id));

          if (progressErr) throw progressErr;

          const progressMap = {};
          progressData?.forEach(row => {
            progressMap[row.matter_id] = row;
          });
          setProgressByMatter(progressMap);

          const { data: questionsData, error: questionsErr } = await supabase
            .from('intake_questions')
            .select('id, question_key, client_visible');

          if (questionsErr) throw questionsErr;

          const { data: responsesData, error: responsesErr } = await supabase
            .from('matter_intake_responses')
            .select('matter_id, question_id, response_json')
            .in('matter_id', matterRows.map(m => m.id));

          if (responsesErr) throw responsesErr;

          const [{ data: rulesData, error: rulesErr }, { data: topicLinks, error: topicLinksErr }] = await Promise.all([
            supabase
              .from('education_recommendation_rules')
              .select('*, resource:education_resources(*)'),
            supabase
              .from('education_question_topics')
              .select(`
                question_key,
                context_label,
                immediate_context,
                display_priority,
                topic:education_topics(
                  id,
                  title,
                  slug,
                  content_category,
                  short_summary,
                  why_we_are_asking,
                  plain_english_explanation,
                  display_priority
                )
              `)
              .eq('is_active', true)
              .order('display_priority', { ascending: true })
          ]);

          if (rulesErr) throw rulesErr;
          if (topicLinksErr) throw topicLinksErr;

          const questionByRuleKey = {};
          questionsData?.filter((question) => question.client_visible !== false).forEach(question => {
            questionByRuleKey[question.question_key] = question;
          });

          const responseByQuestionId = {};
          responsesData?.forEach(response => {
            responseByQuestionId[response.question_id] = response.response_json?.value;
          });

          const matched = [];
          const seenIds = new Set();
          rulesData?.forEach(rule => {
            if (seenIds.has(rule.resource_id)) return;

            const question = questionByRuleKey[rule.question_key];
            if (!question) return;

            const value = responseByQuestionId[question.id];
            const triggered = rule.trigger_value === 'any_nonempty'
              ? value !== undefined && value !== null && value !== ''
              : value === rule.trigger_value;

            if (triggered && rule.resource) {
              matched.push({ ...rule.resource, reason: rule.recommendation_reason });
              seenIds.add(rule.resource_id);
            }
          });

          setRecommendations(matched);
          setTopicRecommendations(buildTopicRecommendations({
            questions: questionsData?.filter((question) => question.client_visible !== false) || [],
            responses: responseByQuestionId,
            topicLinks: topicLinks || []
          }));
        } else {
          setProgressByMatter({});
          setRecommendations([]);
          setTopicRecommendations([]);
        }
      } catch (err) {
        console.error('Client dashboard error:', err);
        setError(err.message || 'Unable to load your client portal.');
      } finally {
        setLoading(false);
      }
    };

    fetchClientDashboard();
  }, [profile]);

  const getStatusLabel = (status) => {
    if (status === 'matter_created') return 'Plan Created';
    if (status === 'intake_in_progress') return 'Intake In Progress';
    if (status === 'intake_submitted') return 'Intake Submitted';
    return status?.replaceAll('_', ' ') || 'Pending';
  };

  const getNextStepText = () => {
    if (matters.length === 0) return 'Your advisor will let you know when a plan has been opened.';

    const activeMatter = matters[0];
    const progress = progressByMatter[activeMatter.id];

    if (!progress || (progress.completion_percent || 0) === 0) {
      return 'Start your intake questionnaire when you are ready.';
    }

    if (progress.status === 'submitted') {
      return 'Your intake has been submitted. The participating law firm can now review your information.';
    }

    return 'Continue your intake questionnaire. Your answers save as you work.';
  };

  const getIntakeAction = (matter) => {
    const progress = progressByMatter[matter.id];
    if (!progress) return 'Start Intake';
    if (progress.status === 'submitted') return 'Review Intake';
    return 'Continue Intake';
  };

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center py-20">
        <SafeIcon icon={FiIcons.FiLoader} className="animate-spin h-10 w-10 text-slate-300 mb-4" />
        <p className="text-slate-500">Loading your portal...</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-slate-900">Client Portal</h1>
        <p className="mt-1 text-sm text-slate-500">
          Welcome, {profile?.first_name}. Review your estate planning workflow and next steps.
        </p>
      </div>

      {error && (
        <div className="bg-red-50 border-l-4 border-red-400 p-4 rounded-md">
          <div className="flex">
            <SafeIcon icon={FiIcons.FiAlertCircle} className="h-5 w-5 text-red-400 mr-2 shrink-0" />
            <p className="text-sm text-red-700 font-medium">{error}</p>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-white p-5 rounded-lg border border-slate-200 shadow-sm">
          <p className="text-xs font-bold text-slate-400 uppercase tracking-widest">Active Plans</p>
          <p className="text-2xl font-bold text-slate-900 mt-2">{matters.length}</p>
        </div>
        <div className="bg-white p-5 rounded-lg border border-slate-200 shadow-sm">
          <p className="text-xs font-bold text-slate-400 uppercase tracking-widest">Next Step</p>
          <p className="text-sm font-medium text-slate-700 mt-2">{getNextStepText()}</p>
        </div>
        <div className="bg-white p-5 rounded-lg border border-slate-200 shadow-sm">
          <p className="text-xs font-bold text-slate-400 uppercase tracking-widest">Guidance</p>
          <p className="text-2xl font-bold text-slate-900 mt-2">{recommendations.length + topicRecommendations.length}</p>
          <p className="text-xs text-slate-500 mt-1">Recommended guidance</p>
        </div>
      </div>

      <div className="bg-white shadow rounded-lg border border-slate-200 overflow-hidden">
        <div className="px-4 py-5 sm:px-6 bg-slate-50 border-b border-slate-200">
          <h3 className="text-lg leading-6 font-medium text-slate-900">Current Plan Status</h3>
          {client && (
            <p className="text-sm text-slate-500 mt-1">
              {client.first_name} {client.last_name}
            </p>
          )}
        </div>

        {matters.length > 0 ? (
          <div className="divide-y divide-slate-100">
            {matters.map((matter) => {
              const progress = progressByMatter[matter.id];
              const completion = progress?.completion_percent || 0;

              return (
                <div key={matter.id} className="p-6 space-y-4">
                  <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
                    <div>
                      <h4 className="font-semibold text-slate-900">{matter.plan_type}</h4>
                      <p className="text-xs text-slate-500 mt-1">
                        Created {format(new Date(matter.created_at), 'MMM d, yyyy')} · {getStatusLabel(matter.status)}
                      </p>
                    </div>
                    <div className="flex flex-col sm:flex-row gap-2">
                      <button
                        onClick={() => navigate(`/client/matters/${matter.id}`)}
                        className="inline-flex items-center justify-center px-4 py-2 text-sm font-medium text-slate-700 bg-white border border-slate-300 rounded-md hover:bg-slate-50 transition-colors"
                      >
                        View Plan
                      </button>
                      <button
                        onClick={() => navigate(progress?.status === 'submitted' ? `/matters/${matter.id}/intake-summary` : `/matters/${matter.id}/intake`)}
                        className="inline-flex items-center justify-center px-4 py-2 text-sm font-medium text-white bg-slate-800 rounded-md hover:bg-slate-900 transition-colors"
                      >
                        {getIntakeAction(matter)}
                        <SafeIcon icon={FiIcons.FiChevronRight} className="ml-2 h-4 w-4" />
                      </button>
                    </div>
                  </div>

                  <div>
                    <div className="flex justify-between text-xs font-bold uppercase tracking-widest text-slate-400 mb-2">
                      <span>Intake Progress</span>
                      <span>{completion}%</span>
                    </div>
                    <div className="w-full bg-slate-100 rounded-full h-2 overflow-hidden">
                      <div
                        className="bg-blue-600 h-2 rounded-full transition-all duration-500"
                        style={{ width: `${completion}%` }}
                      />
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="p-6">
            <div className="flex items-center justify-center p-8 border-2 border-dashed border-slate-200 rounded-lg">
              <div className="text-center">
                <SafeIcon icon={FiIcons.FiClock} className="mx-auto h-12 w-12 text-slate-300 mb-3" />
                <h3 className="text-sm font-medium text-slate-900">No Active Plan</h3>
                <p className="mt-1 text-sm text-slate-500">Your advisor has not initiated an estate plan yet.</p>
              </div>
            </div>
          </div>
        )}
      </div>

      <div className="bg-white shadow rounded-lg border border-slate-200">
        <div className="px-4 py-5 sm:px-6 border-b border-slate-200">
          <h3 className="text-lg leading-6 font-medium text-slate-900">Next Steps</h3>
        </div>
        <div className="p-6 text-slate-600 text-sm">
          Complete your intake questionnaire so the participating law firm can review your information. This platform provides technology and workflow support; financial advisors do not provide legal advice through this platform.
        </div>
      </div>

      {matters.length > 0 && (
        <AssignedLearningPanel
          matterIds={matters.map((matter) => matter.id)}
          matters={matters}
          emptyText="Your advisor or attorney may assign short learning topics here as your plan develops."
        />
      )}

      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-bold text-slate-900">Recommended Learning</h3>
          <span className="text-xs font-bold uppercase tracking-widest text-slate-400">Based on intake answers</span>
        </div>
        {topicRecommendations.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {topicRecommendations.slice(0, 6).map((topic) => (
              <RecommendedTopicCard
                key={topic.id}
                topic={topic}
                onOpen={(selectedTopic) => navigate(buildLearningTopicPath({ slug: selectedTopic.slug }))}
              />
            ))}
          </div>
        ) : (
          <RecommendationEmptyState title="No recommended learning yet">
            As you answer more intake questions, relevant estate planning topics may appear here.
          </RecommendationEmptyState>
        )}
      </div>

      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-bold text-slate-900">Recommended Guidance</h3>
          <span className="text-xs font-bold uppercase tracking-widest text-slate-400">Based on intake answers</span>
        </div>
        {recommendations.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {recommendations.map(resource => (
              <div key={resource.id} className="bg-white rounded-lg border border-slate-200 shadow-sm p-5 flex flex-col">
                <div className="flex items-center justify-between mb-3">
                  <span className="px-2 py-0.5 bg-blue-50 text-blue-700 text-[10px] font-bold uppercase rounded">{resource.resource_type}</span>
                  <span className="text-xs text-slate-400">{resource.estimated_minutes} min</span>
                </div>
                <h4 className="font-bold text-slate-900">{resource.title}</h4>
                <p className="text-xs text-slate-500 mt-2 flex-1">{resource.short_description}</p>
                <button onClick={() => navigate(`/education/${resource.slug}`)} className="mt-4 py-2 text-xs font-bold uppercase tracking-widest text-slate-700 bg-slate-50 rounded hover:bg-slate-100 transition-colors">
                  View Resource
                </button>
              </div>
            ))}
          </div>
        ) : (
          <RecommendationEmptyState title="No recommended resources yet">
            Helpful articles and resources may appear here when they match your intake answers.
          </RecommendationEmptyState>
        )}
      </div>
    </div>
  );
};

export default ClientDashboard;
